import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import { 
  calculateMatchStatsStatus, 
  calculatePlayerStatsStatus,
  calculateTimelineStatus,
  calculateKeyEventsStatus,
  calculateVisualisationStatus,
  calculateVideoTagsStatus,
  OperationStatus
} from '../../../shared/utils/configStatusUtils';
import { calculatedStatsService } from '../../../shared/services/calculatedStatsService';
import { playerScoresheetService } from '../../../shared/services/playerScoresheetService';
import { KeyEventsSyncService } from '../../../shared/services/keyEventsSyncService';
import { VideoTagsSyncService } from '../../../shared/services/videoTagsSyncService';
import { hasValidPositionData } from '../../../shared/utils/positionExtraction';

interface ConfigTabProps {
  matchId?: string;
  homeTeamId?: string;
  awayTeamId?: string;
  onResetMatch?: () => void;
}

interface OperationState {
  status: OperationStatus;
  errorMessage?: string;
  lastChecked?: Date;
}

const ConfigTab: React.FC<ConfigTabProps> = ({ matchId, homeTeamId, awayTeamId, onResetMatch }) => {
  const [matchStatsStatus, setMatchStatsStatus] = useState<OperationState>({ status: 'sync_required' });
  const [playerStatsStatus, setPlayerStatsStatus] = useState<OperationState>({ status: 'sync_required' });
  const [timelineStatus, setTimelineStatus] = useState<OperationState>({ status: 'sync_required' });
  const [keyEventsStatus, setKeyEventsStatus] = useState<OperationState>({ status: 'sync_required' });
  const [visualisationStatus, setVisualisationStatus] = useState<OperationState>({ status: 'sync_required' });
  const [videoTagsStatus, setVideoTagsStatus] = useState<OperationState>({ status: 'sync_required' });

  const [isComputingStats, setIsComputingStats] = useState(false);
  const [isPushingEvents, setIsPushingEvents] = useState(false);

  // Load initial statuses
  useEffect(() => {
    if (matchId) {
      refreshAllStatuses();
    }
  }, [matchId, homeTeamId, awayTeamId]);

  const refreshAllStatuses = async () => {
    if (!matchId) return;

    try {
      console.log('Refreshing all statuses for match:', matchId);
      const [
        matchStats,
        playerStats,
        timeline,
        keyEvents,
        visualisation,
        videoTags
      ] = await Promise.all([
        homeTeamId && awayTeamId 
          ? calculateMatchStatsStatus(matchId, homeTeamId, awayTeamId)
          : Promise.resolve({ status: 'sync_required' as OperationStatus }),
        calculatePlayerStatsStatus(matchId),
        calculateTimelineStatus(matchId),
        calculateKeyEventsStatus(matchId),
        calculateVisualisationStatus(matchId),
        calculateVideoTagsStatus(matchId)
      ]);

      console.log('Status refresh results:', {
        matchStats,
        playerStats,
        timeline,
        keyEvents,
        visualisation,
        videoTags
      });

      setMatchStatsStatus(matchStats);
      setPlayerStatsStatus(playerStats);
      setTimelineStatus(timeline);
      setKeyEventsStatus(keyEvents);
      setVisualisationStatus(visualisation);
      setVideoTagsStatus(videoTags);
    } catch (error) {
      console.error('Error refreshing statuses:', error);
    }
  };

  const handleComputeAllStats = async () => {
    if (!matchId || !homeTeamId || !awayTeamId) {
      return;
    }

    setIsComputingStats(true);
    setMatchStatsStatus({ status: 'running' });
    setPlayerStatsStatus({ status: 'running' });

    try {
      // Run both calculations in parallel
      const [matchStatsResult, playerStatsResult] = await Promise.allSettled([
        calculatedStatsService.calculateAndSaveAllStats(matchId, homeTeamId, awayTeamId),
        playerScoresheetService.calculateAndSaveAllPlayersStats(matchId)
      ]);

      // Update statuses based on results
      if (matchStatsResult.status === 'fulfilled') {
        setMatchStatsStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        setMatchStatsStatus({ 
          status: 'error', 
          errorMessage: matchStatsResult.reason?.message || 'Failed to calculate match stats',
          lastChecked: new Date()
        });
      }

      if (playerStatsResult.status === 'fulfilled') {
        setPlayerStatsStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        setPlayerStatsStatus({ 
          status: 'error', 
          errorMessage: playerStatsResult.reason?.message || 'Failed to calculate player stats',
          lastChecked: new Date()
        });
      }

      // Refresh all statuses after a longer delay to ensure calculations complete
      // and database updates are reflected
      setTimeout(() => {
        refreshAllStatuses();
      }, 2000);
    } catch (error) {
      console.error('Error computing stats:', error);
    } finally {
      setIsComputingStats(false);
    }
  };

  const handlePushAllEvents = async () => {
    if (!matchId) {
      return;
    }

    setIsPushingEvents(true);
    setTimelineStatus({ status: 'running' });
    setKeyEventsStatus({ status: 'running' });
    setVisualisationStatus({ status: 'running' });
    setVideoTagsStatus({ status: 'running' });

    try {
      // Get current user
      const { data: { user } } = await (await import('../../../shared/lib/supabase')).supabase.auth.getUser();

      // Fetch all logs in batches to avoid 406 errors
      const supabaseClient = (await import('../../../shared/lib/supabase')).supabase;
      const allLogs: any[] = [];
      let offset = 0;
      const batchSize = 500;
      let hasMore = true;

      while (hasMore) {
        const { data: batch, error: batchError } = await supabaseClient
          .from('scoresheet_logs')
          .select(`
            id,
            match_id,
            team_id,
            stat_type_id,
            player_id,
            match_minute,
            action,
            new_value,
            timestamp,
            user_id,
            position_data,
            video_time_seconds,
            video_id,
            manual_tags,
            stat_types!inner(stat_key, display_name, category, event_result)
          `)
          .eq('match_id', matchId)
          .eq('is_active', true)
          .order('timestamp', { ascending: true })
          .range(offset, offset + batchSize - 1);

        if (batchError) {
          throw new Error(`Failed to fetch scoresheet logs: ${batchError.message}`);
        }

        if (batch && batch.length > 0) {
          allLogs.push(...batch);
          offset += batchSize;
          hasMore = batch.length === batchSize;
        } else {
          hasMore = false;
        }
      }


      const logs = allLogs || [];

      // Filter logs for each operation
      const keyEventLogs = logs.filter(log => {
        const statType = log.stat_types as any;
        const KEY_EVENT_STAT_TYPES = [
          'goal_scored', 'shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored', 'penalty_missed',
          'penalty_shootout_scored', 'penalty_shootout_missed', 'own_goal',
          'yellow_card', 'red_card', 'substitution',
          'kick_off', 'end_1st_half', 'start_2nd_half', 'start_extra_time_1st_half',
          'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout', 'full_time'
        ];
        return KEY_EVENT_STAT_TYPES.includes(statType.stat_key);
      });

      const timelineLogs = logs.filter(log => {
        const statType = log.stat_types as any;
        const EXCLUDED = [
          'short_pass_success', 'short_pass_failure', 'short_pass_backward_success',
          'short_pass_backward_failure', 'long_pass_success', 'long_pass_failure',
          'long_pass_backward_success', 'long_pass_backward_failure',
          'tackle_success', 'tackle_failure', 'throw_in',
          'interception', 'clearance'
        ];
        return !EXCLUDED.includes(statType.stat_key);
      });

      const visualizationLogs = logs.filter(log => hasValidPositionData(log.position_data));

      const videoTagLogs = logs.filter(log => log.video_time_seconds != null && log.video_id);

      // Run all sync operations in parallel
      const [timelineResult, keyEventsResult, visualisationResult, videoTagsResult] = await Promise.allSettled([
        KeyEventsSyncService.syncMatchTimeline(matchId, timelineLogs, user?.id),
        KeyEventsSyncService.syncKeyMatchEvents(matchId, keyEventLogs, user?.id),
        KeyEventsSyncService.syncMatchVisualization(matchId, visualizationLogs, user?.id),
        VideoTagsSyncService.syncVideoTagsFromScoresheetLogs(matchId, videoTagLogs, user?.id)
      ]);

      // Log results for debugging
      console.log('Sync Results:', {
        timeline: timelineResult.status === 'fulfilled' ? timelineResult.value : timelineResult.reason,
        keyEvents: keyEventsResult.status === 'fulfilled' ? keyEventsResult.value : keyEventsResult.reason,
        visualisation: visualisationResult.status === 'fulfilled' ? visualisationResult.value : visualisationResult.reason,
        videoTags: videoTagsResult.status === 'fulfilled' ? videoTagsResult.value : videoTagsResult.reason
      });

      // Update statuses based on results
      if (timelineResult.status === 'fulfilled') {
        console.log('Timeline sync successful:', timelineResult.value);
        setTimelineStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        console.error('Timeline sync failed:', timelineResult.reason);
        setTimelineStatus({ 
          status: 'error', 
          errorMessage: timelineResult.reason?.message || 'Failed to sync timeline',
          lastChecked: new Date()
        });
      }

      if (keyEventsResult.status === 'fulfilled') {
        console.log('Key Events sync successful:', keyEventsResult.value);
        setKeyEventsStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        console.error('Key Events sync failed:', keyEventsResult.reason);
        setKeyEventsStatus({ 
          status: 'error', 
          errorMessage: keyEventsResult.reason?.message || 'Failed to sync key events',
          lastChecked: new Date()
        });
      }

      if (visualisationResult.status === 'fulfilled') {
        console.log('Visualisation sync successful:', visualisationResult.value);
        setVisualisationStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        console.error('Visualisation sync failed:', visualisationResult.reason);
        setVisualisationStatus({ 
          status: 'error', 
          errorMessage: visualisationResult.reason?.message || 'Failed to sync visualisation',
          lastChecked: new Date()
        });
      }

      if (videoTagsResult.status === 'fulfilled') {
        console.log('Video Tags sync successful:', videoTagsResult.value);
        setVideoTagsStatus({ status: 'synced', lastChecked: new Date() });
      } else {
        console.error('Video Tags sync failed:', videoTagsResult.reason);
        setVideoTagsStatus({ 
          status: 'error', 
          errorMessage: videoTagsResult.reason?.message || 'Failed to sync video tags',
          lastChecked: new Date()
        });
      }

      // Refresh all statuses after a longer delay to ensure sync operations complete
      // and database updates are reflected
      setTimeout(() => {
        refreshAllStatuses();
      }, 2000);
    } catch (error) {
      console.error('Error pushing events:', error);
    } finally {
      setIsPushingEvents(false);
    }
  };

  const getStatusBadge = (state: OperationState) => {
    const { status } = state;
    
    if (status === 'running') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Running
        </span>
      );
    }

    if (status === 'synced') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Synced
        </span>
      );
    }

    if (status === 'sync_required') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Sync Required
        </span>
      );
    }

    if (status === 'error') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Error
        </span>
      );
    }

    return null;
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Compute Statistics Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Compute Statistics</h3>
            <Button
              onClick={handleComputeAllStats}
              disabled={isComputingStats || !matchId || !homeTeamId || !awayTeamId}
              variant="primary"
              size="sm"
            >
              {isComputingStats ? 'Computing...' : 'Run'}
            </Button>
          </div>

          <div className="space-y-3">
            {/* Match Stats */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Match Stats</p>
                {matchStatsStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{matchStatsStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(matchStatsStatus)}
              </div>
            </div>

            {/* Player Stats */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Player Stats</p>
                {playerStatsStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{playerStatsStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(playerStatsStatus)}
              </div>
            </div>
          </div>
        </div>

        {/* Push Events Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Push Events</h3>
            <Button
              onClick={handlePushAllEvents}
              disabled={isPushingEvents || !matchId}
              variant="primary"
              size="sm"
            >
              {isPushingEvents ? 'Pushing...' : 'Run'}
            </Button>
          </div>

          <div className="space-y-3">
            {/* Timeline */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Timeline</p>
                {timelineStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{timelineStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(timelineStatus)}
              </div>
            </div>

            {/* Key Events */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Key Events</p>
                {keyEventsStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{keyEventsStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(keyEventsStatus)}
              </div>
            </div>

            {/* Visualisation */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Visualisation</p>
                {visualisationStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{visualisationStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(visualisationStatus)}
              </div>
            </div>

            {/* Video Tags */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Video Tags</p>
                {videoTagsStatus.errorMessage && (
                  <p className="text-xs text-red-600 mt-1">{videoTagsStatus.errorMessage}</p>
                )}
              </div>
              <div className="ml-4">
                {getStatusBadge(videoTagsStatus)}
              </div>
            </div>
          </div>
        </div>

        {/* Reset Match Section */}
        {onResetMatch && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset Match</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800 mb-2">
                        Warning: This action will permanently delete all match data from the following tables:
                      </p>
                      <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                        <li><code className="bg-red-100 px-1 rounded">match_visualization</code> - All visualization events</li>
                        <li><code className="bg-red-100 px-1 rounded">match_timeline</code> - All timeline events</li>
                        <li><code className="bg-red-100 px-1 rounded">key_match_events</code> - All key match events</li>
                        <li><code className="bg-red-100 px-1 rounded">match_video_tags</code> - All video tags</li>
                        <li><code className="bg-red-100 px-1 rounded">scoresheet_logs</code> - All scoresheet log entries</li>
                        <li><code className="bg-red-100 px-1 rounded">match_videos</code> - All match videos</li>
                        <li><code className="bg-red-100 px-1 rounded">scoresheet</code> - All scoresheet values</li>
                        <li><code className="bg-red-100 px-1 rounded">player_scoresheet</code> - All player-level scoresheet data</li>
                        <li><code className="bg-red-100 px-1 rounded">match_lineup</code> - Substitution data (play_start_time, play_stop_time)</li>
                      </ul>
                      <p className="text-xs text-red-700 mt-3 font-medium">
                        Match scores and status will be reset to 0-0 and "upcoming" in the <code className="bg-red-100 px-1 rounded">matches</code> table.
                      </p>
                      <p className="text-xs text-red-600 mt-2 italic">
                        This action cannot be undone. Please ensure you have backed up any important data before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={onResetMatch}
                disabled={!matchId}
                variant="danger"
                size="sm"
              >
                Reset Match
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigTab;
